import React from "react";
import {
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
} from "@mui/material";
import dayjs from "dayjs";
import ActionFilter from "../../components/ActionFilter";
import CommonPagination from "../../components/Table/CommonPagination";
import { limitChar } from "../../utils/common";
import {
  getChallengeBackgroundUrl,
  normalizeStickers,
  getChallengeStatus,
  isFeedGroupSizeEligibleCategory,
} from "./helpers";
import { normalizeFeedGroupSize } from "../../components/FormElements/FeedGroupSizeField";

const headerCellSx = {
  fontSize: {
    xs: "14px",
    sm: "15px",
    md: "15px",
    lg: "16px",
    xl: "16px",
  },
  color: "#000",
  fontWeight: 600,
  padding: {
    xs: "10px 5px",
    sm: "15px 10px",
    md: "15px 10px",
    lg: "20px 15px",
    xl: "20px 15px",
  },
  backgroundColor: "#e0e0e0",
};

const bodyCellSx = {
  fontSize: {
    xs: "12px",
    sm: "13px",
    md: "14px",
    lg: "15px",
    xl: "15px",
  },
  color: "#333",
  padding: {
    xs: "10px 5px",
    sm: "10px",
    md: "10px",
    lg: "10px 15px",
    xl: "10px 15px",
  },
};

const ChallengeTable = ({
  classes,
  challenges,
  canViewActivity,
  handleViewChallenge,
  handleEditChallenge,
  handleDeleteChallenge,
  limit,
  totalChallenges,
  page,
  handlePageChange,
}) => {
  return (
    <Box
      sx={{
        padding: "15px 15px 0 15px",
        borderRadius: "6px",
        boxShadow:
          "rgba(0, 0, 0, 0.05) 0 6px 24px 0, rgba(0, 0, 0, 0.08) 0 0 0 1px",
        marginTop: "10px",
        width: "100%",
        boxSizing: "border-box",
      }}
    >
      <TableContainer component={Paper} className={classes.tableContainer}>
        <Table className={classes.table}>
          <TableHead>
            <TableRow className={classes.stickyHeader}>
              <TableCell sx={headerCellSx}>Category Logo</TableCell>
              <TableCell sx={headerCellSx}>Background</TableCell>
              <TableCell sx={headerCellSx}>Stickers</TableCell>
              <TableCell sx={headerCellSx}>Category Name</TableCell>
              <TableCell sx={headerCellSx}>Challenge Name</TableCell>
              <TableCell sx={headerCellSx}>Theme</TableCell>
              <TableCell sx={headerCellSx}>Start Date</TableCell>
              <TableCell sx={headerCellSx}>End Date</TableCell>
              <TableCell sx={headerCellSx}>Status</TableCell>
              <TableCell sx={headerCellSx}>Feed group</TableCell>
              {canViewActivity && (
                <TableCell sx={headerCellSx}>Post Count</TableCell>
              )}
              <TableCell sx={headerCellSx}>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {challenges.length === 0 ? (
              <TableRow>
                <TableCell
                  colSpan={canViewActivity ? 12 : 11}
                  sx={{
                    textAlign: "center",
                    height: "calc(100vh - 280px)",
                    ...bodyCellSx,
                  }}
                >
                  No challenges found
                </TableCell>
              </TableRow>
            ) : (
              challenges.map((challenge) => (
                <TableRow key={challenge._id}>
                  <TableCell sx={bodyCellSx}>
                    {challenge.category && challenge.category.logo ? (
                      <img
                        src={challenge.category.logo}
                        alt="Category Logo"
                        width="25px"
                      />
                    ) : (
                      <span>No Logo</span>
                    )}
                  </TableCell>
                  <TableCell sx={bodyCellSx}>
                    {getChallengeBackgroundUrl(challenge) ? (
                      <img
                        src={getChallengeBackgroundUrl(challenge)}
                        alt="Challenge Background"
                        width="40px"
                        height="24px"
                        style={{ objectFit: "cover", borderRadius: "4px" }}
                      />
                    ) : (
                      <span>No Image</span>
                    )}
                  </TableCell>
                  <TableCell sx={bodyCellSx}>
                    {normalizeStickers(challenge).length ? (
                      <Box
                        sx={{
                          display: "flex",
                          gap: "4px",
                          flexWrap: "wrap",
                          maxWidth: "150px",
                        }}
                      >
                        {normalizeStickers(challenge).map(
                          (sticker, stickerIndex) => (
                            <img
                              key={sticker.mediaId || sticker.url}
                              src={sticker.url}
                              alt={`Sticker ${stickerIndex + 1}`}
                              width="24px"
                              height="24px"
                              style={{
                                objectFit: "contain",
                                borderRadius: "4px",
                                backgroundColor: "#f4f4f4",
                              }}
                            />
                          )
                        )}
                      </Box>
                    ) : (
                      <span>None</span>
                    )}
                  </TableCell>
                  <TableCell sx={bodyCellSx}>
                    {challenge.category
                      ? limitChar(challenge?.category?.name)
                      : "No Category Name"}
                  </TableCell>
                  <TableCell sx={bodyCellSx}>
                    {limitChar(challenge?.title)}
                  </TableCell>
                  <TableCell sx={bodyCellSx}>
                    {challenge?.theme?.title ? (
                      limitChar(challenge.theme.title)
                    ) : (
                      <span style={{ color: "#999" }}>—</span>
                    )}
                  </TableCell>
                  <TableCell sx={bodyCellSx}>
                    {challenge?.startDate
                      ? dayjs(challenge.startDate).format("DD MMM YYYY - h:mm A")
                      : "TBD"}
                  </TableCell>
                  <TableCell sx={bodyCellSx}>
                    {challenge?.endDate
                      ? dayjs(challenge.endDate).format("DD MMM YYYY - h:mm A")
                      : "TBD"}
                  </TableCell>
                  <TableCell sx={bodyCellSx}>
                    {(() => {
                      const statusInfo = getChallengeStatus(
                        challenge.startDate,
                        challenge.endDate
                      );
                      return (
                        <Chip
                          label={statusInfo.status}
                          size="small"
                          sx={{
                            backgroundColor: statusInfo.bgColor,
                            color:
                              statusInfo.color === "warning"
                                ? "#856404"
                                : statusInfo.color === "error"
                                ? "#721c24"
                                : statusInfo.color === "default"
                                ? "#41464b"
                                : "#0c5460",
                            fontWeight: "600",
                            fontSize: "12px",
                            height: "24px",
                          }}
                        />
                      );
                    })()}
                  </TableCell>
                  <TableCell sx={bodyCellSx}>
                    {isFeedGroupSizeEligibleCategory(challenge.category)
                      ? normalizeFeedGroupSize(challenge.feedGroupSize) ??
                        "Default (category)"
                      : "—"}
                  </TableCell>
                  {canViewActivity && (
                    <TableCell
                      sx={{
                        ...bodyCellSx,
                        textAlign: "center",
                      }}
                    >
                      {challenge.postCount || 0}
                    </TableCell>
                  )}
                  <TableCell sx={bodyCellSx}>
                    <ActionFilter
                      onView={() => handleViewChallenge(challenge)}
                      onEdit={() => handleEditChallenge(challenge)}
                      onDelete={() => handleDeleteChallenge(challenge)}
                      index={challenge._id}
                    />
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>
      <CommonPagination
        rowsPerPage={limit}
        count={totalChallenges}
        page={page}
        onPageChange={handlePageChange}
      />
    </Box>
  );
};

export default ChallengeTable;
